import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import type { CreateSupportGrantRequest, SupportGrantResponse } from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { PageHeader } from '../components/PageHeader';
import { ErrorState } from '../components/states/StateViews';
import { useAuthStore } from '../auth/authStore';

/**
 * Support access (SEC-003, FR-110) — the clinic's side of the platform-blind promise.
 *
 * This screen is a trust artefact as much as a control panel, so it says what it does rather than
 * assuming anyone reads a contract: what the operator will be able to see, what they will not, when
 * it stops, and whether they actually looked. The "used" column is the part a clinic will care
 * about after the fact.
 */
export function SupportAccessPage() {
  const { t } = useTranslation();
  const clinicId = useAuthStore((state) => state.activeClinicId);

  const grants = useApiQuery<SupportGrantResponse[]>(
    queryKeys.supportGrants(clinicId ?? ''),
    `/clinics/${clinicId}/support-grants`,
    { enabled: Boolean(clinicId) },
  );

  const grant = useApiMutation<SupportGrantResponse, CreateSupportGrantRequest>(
    (body) => ({ path: `/clinics/${clinicId}/support-grants`, options: { method: 'POST', body } }),
    { invalidates: [queryKeys.supportGrants(clinicId ?? '')] },
  );

  const revoke = useApiMutation<SupportGrantResponse, string>(
    (grantId) => ({
      path: `/clinics/${clinicId}/support-grants/${grantId}`,
      options: { method: 'DELETE' },
    }),
    { invalidates: [queryKeys.supportGrants(clinicId ?? '')] },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.supportGrants')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader title={t('nav.items.supportGrants')} description={t('support.description')} />

      <div className="callout" style={{ marginBlockEnd: 'var(--space-5)' }}>
        <p className="state__title">{t('support.promiseTitle')}</p>
        <ul style={{ marginBlock: 'var(--space-3)', paddingInlineStart: 'var(--space-5)' }}>
          <li>{t('support.promiseReadOnly')}</li>
          <li>{t('support.promiseNoClinical')}</li>
          <li>{t('support.promiseTimeBoxed')}</li>
          <li>{t('support.promiseAudited')}</li>
        </ul>
      </div>

      <form
        className="panel"
        style={{ marginBlockEnd: 'var(--space-5)' }}
        onSubmit={(event) => {
          event.preventDefault();
          const form = new FormData(event.currentTarget);
          const hours = Number(form.get('hours') ?? 24);
          grant.mutate({
            granteeEmail: String(form.get('granteeEmail') ?? '').trim(),
            reason: String(form.get('reason') ?? '').trim(),
            expiresAt: new Date(Date.now() + hours * 60 * 60 * 1000).toISOString(),
          });
        }}
      >
        <div className="form-grid">
          <label className="field">
            <span className="field__label">{t('support.operatorEmail')}</span>
            <input type="email" name="granteeEmail" className="field__input ltr" required autoComplete="off" />
          </label>
          <label className="field">
            <span className="field__label">{t('support.reason')}</span>
            <input name="reason" className="field__input" required maxLength={400} />
            <span className="field__hint">{t('support.reasonHint')}</span>
          </label>
          <label className="field">
            <span className="field__label">{t('support.duration')}</span>
            <select name="hours" className="field__input" defaultValue="24">
              <option value="4">{t('support.hours', { n: 4 })}</option>
              <option value="24">{t('support.hours', { n: 24 })}</option>
              <option value="72">{t('support.hours', { n: 72 })}</option>
              <option value="168">{t('support.days', { n: 7 })}</option>
            </select>
            <span className="field__hint">{t('support.durationHint')}</span>
          </label>
        </div>

        {grant.error ? <ErrorState error={grant.error} /> : null}

        <div style={{ marginBlockStart: 'var(--space-4)' }}>
          <button type="submit" className="button button--primary" disabled={grant.isPending}>
            {grant.isPending ? t('branches.saving') : t('support.grant')}
          </button>
        </div>
      </form>

      <AsyncSection
        query={grants}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('support.emptyTitle')}
        emptyDescription={t('support.emptyDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('support.operator')}</th>
                  <th scope="col">{t('support.reason')}</th>
                  <th scope="col">{t('common.status')}</th>
                  <th scope="col">{t('support.expires')}</th>
                  <th scope="col">{t('support.used')}</th>
                  <th scope="col">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <th scope="row" className="ltr">
                      {row.granteeEmail}
                    </th>
                    <td>{row.reason}</td>
                    <td>
                      <span className={statusBadge(row.status)}>{t(`support.status.${row.status ?? 'EXPIRED'}`)}</span>
                    </td>
                    <td className="numeric">{formatInstant(row.expiresAt)}</td>
                    <td>
                      {row.useCount ? (
                        <span title={formatInstant(row.lastUsedAt)}>
                          {t('support.usedTimes', { count: row.useCount })}
                        </span>
                      ) : (
                        <span className="badge badge--muted">{t('support.neverUsed')}</span>
                      )}
                    </td>
                    <td>
                      {row.status === 'ACTIVE' ? (
                        <button
                          type="button"
                          className="button button--ghost"
                          disabled={revoke.isPending}
                          onClick={() => revoke.mutate(row.id ?? '')}
                        >
                          {t('support.revoke')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {revoke.error ? <ErrorState error={revoke.error} /> : null}
    </>
  );
}

function statusBadge(status: SupportGrantResponse['status']): string {
  if (status === 'ACTIVE') {
    return 'badge';
  }
  if (status === 'REVOKED') {
    return 'badge badge--danger';
  }
  return 'badge badge--muted';
}

/** Dates render in Western digits even in Arabic (ASM-004), so the raw ISO date is trimmed, not localised away. */
function formatInstant(value: string | undefined | null): string {
  if (!value) {
    return '—';
  }
  return value.slice(0, 16).replace('T', ' ');
}
